import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { products, reviews } from '../../mocks/products';

const faqs = [
  {
    id: 1,
    question: "What's in your nectar mix?",
    answer: "Our dry nectar mix contains only two ingredients: pure cane sugar and a trace of natural red coloring derived from beet extract. No artificial dyes, no preservatives, no corn syrup — just the clean, simple formula hummingbirds love. It's the closest thing to natural flower nectar you can give them."
  },
  {
    id: 2,
    question: "How long does the mix last once prepared?",
    answer: "Once mixed with water, fresh nectar should be used within 3–5 days when stored in the refrigerator, or 1–2 days if left outside in warm weather. The dry pouch itself has a shelf life of 12+ months when stored in a cool, dry place — so you can stock up without worry!"
  },
  {
    id: 3,
    question: "When should I start feeding hummingbirds?",
    answer: "Put your feeder out 1–2 weeks before hummingbirds are expected in your area. In the southern US, that can be as early as late February or March. In northern states and Canada, late April to May is typical. Check our Migration Map for exact arrival times in your state!"
  },
  {
    id: 4,
    question: "How do I mix the nectar?",
    answer: "It's super easy! Just dissolve the dry mix in warm water — no boiling required. Our pouches come pre-measured: one pouch makes the exact amount listed on the label. Stir until fully dissolved, let it cool, then fill your feeder. That's it!"
  },
  {
    id: 5,
    question: "Is your nectar safe for hummingbirds?",
    answer: "Absolutely. Our formula is vet-reviewed and free from harmful additives. We never use red dye #40 or artificial sweeteners, which can be harmful to hummingbirds. Pure cane sugar and water is exactly what wildlife experts recommend."
  },
  {
    id: 6,
    question: "Do you offer free shipping?",
    answer: "Yes! We offer free shipping on all orders $25 or more. Orders ship within 1–2 business days and typically arrive in 3–5 business days depending on your location."
  }
];

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupEmail, setPopupEmail] = useState('');
  const [popupSubmitted, setPopupSubmitted] = useState(false);
  const [popupSubmitting, setPopupSubmitting] = useState(false);
  const [footerEmail, setFooterEmail] = useState('');
  const [footerSubmitted, setFooterSubmitted] = useState(false);
  const [footerSubmitting, setFooterSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem('newsletter_dismissed');
    if (dismissed) return;
    const timer = setTimeout(() => setShowPopup(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  const dismissPopup = () => {
    setShowPopup(false);
    sessionStorage.setItem('newsletter_dismissed', 'true');
  };

  const handlePopupSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!popupEmail) return;
    setPopupSubmitting(true);
    try {
      const body = new URLSearchParams({ email: popupEmail });
      await fetch('https://readdy.ai/api/form/d6gvq42u8ojn6g516ld0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setPopupSubmitted(true);
      setTimeout(() => dismissPopup(), 2500);
    } catch {
      // silent
    } finally {
      setPopupSubmitting(false);
    }
  };

  const handleFooterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!footerEmail) return;
    setFooterSubmitting(true);
    try {
      const body = new URLSearchParams({ email: footerEmail });
      await fetch('https://readdy.ai/api/form/d6gvq42u8ojn6g516ld0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setFooterSubmitted(true);
    } catch {
      // silent
    } finally {
      setFooterSubmitting(false);
    }
  };

  const toggleFaq = (id: number) => {
    setOpenFaq(prev => (prev === id ? null : id));
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">

      {/* Email Capture Popup */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={dismissPopup}></div>
          <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl max-w-lg w-full mx-4 animate-fade-in">
            <div className="relative h-40 sm:h-48 overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/cf56307f011d6b3f16b7e28f3ddd4e87.jpg"
                alt="Hummingbird"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/50"></div>
              <button
                onClick={dismissPopup}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/40 transition-all cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
              <div className="absolute bottom-4 left-6">
                <span className="px-3 py-1 bg-[#FF6B6B] text-white text-xs font-bold rounded-full tracking-widest uppercase whitespace-nowrap">
                  Free Tips Inside
                </span>
              </div>
            </div>
            <div className="p-6 sm:p-8">
              {!popupSubmitted ? (
                <>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#2C3E50] mb-2 leading-tight">
                    Get Nectar Tips &amp; Migration Dates
                  </h3>
                  <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                    Join 12,000+ bird lovers. We'll send you seasonal feeding tips, hummingbird migration alerts for your region, and exclusive subscriber discounts.
                  </p>
                  <form data-readdy-form onSubmit={handlePopupSubmit} className="flex flex-col gap-3">
                    <input
                      type="email"
                      name="email"
                      required
                      value={popupEmail}
                      onChange={e => setPopupEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="w-full px-5 py-3 rounded-full border border-gray-200 text-sm text-[#2C3E50] focus:outline-none focus:border-[#FF6B6B] transition-colors"
                    />
                    <button
                      type="submit"
                      disabled={popupSubmitting}
                      className="w-full px-5 py-3 bg-[#FF6B6B] text-white rounded-full font-semibold text-sm hover:bg-[#ff5252] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {popupSubmitting ? 'Subscribing...' : 'Yes! Send Me Tips 🐦'}
                    </button>
                  </form>
                  <p className="text-center text-xs text-gray-400 mt-4">No spam, ever. Unsubscribe anytime.</p>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#FFF0F0] rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-[#FF6B6B]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#2C3E50] mb-2">You're on the list! 🎉</h3>
                  <p className="text-gray-500 text-sm">Watch your inbox for nectar tips and migration alerts.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_669d202f2dc28622ce5725af4f53ca9a_96732d61.jpg"
            alt="Hummingbird drinking nectar from flower"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/50"></div>
        </div>

        {/* Navigation */}
        <nav className="absolute top-0 left-0 right-0 z-20 px-4 sm:px-8 py-4 sm:py-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src="https://public.readdy.ai/ai/img_res/b04a437c-0005-402a-b32c-9c2676d2dc8b.png"
                alt="Front Porch Nectar Logo"
                className="h-10 sm:h-14 w-auto max-w-[160px] sm:max-w-none object-contain"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-white font-medium hover:text-white/80 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
              <Link to="/product/15oz" className="text-white font-medium hover:text-white/80 transition-colors whitespace-nowrap cursor-pointer">Products</Link>
              <Link to="/migration" className="text-white font-medium hover:text-white/80 transition-colors whitespace-nowrap cursor-pointer">Migration</Link>
              <Link to="/checkout" className="text-white font-medium hover:text-white/80 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-shopping-cart-line text-2xl"></i>
              </Link>
            </div>

            {/* Mobile Nav Icons */}
            <div className="flex md:hidden items-center gap-4">
              <Link to="/checkout" className="text-white cursor-pointer">
                <i className="ri-shopping-cart-line text-2xl"></i>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(prev => !prev)}
                className="text-white cursor-pointer w-8 h-8 flex items-center justify-center"
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Dropdown Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-3 mx-4 bg-white/95 backdrop-blur-sm rounded-2xl overflow-hidden shadow-xl">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block px-6 py-4 text-[#2C3E50] font-medium border-b border-gray-100 cursor-pointer">Home</Link>
              <Link to="/product/15oz" onClick={() => setMobileMenuOpen(false)} className="block px-6 py-4 text-[#2C3E50] font-medium border-b border-gray-100 cursor-pointer">Products</Link>
              <Link to="/migration" onClick={() => setMobileMenuOpen(false)} className="block px-6 py-4 text-[#2C3E50] font-medium cursor-pointer">Migration</Link>
            </div>
          )}
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 w-full pt-24 pb-12">
          <div className="inline-block mb-4 sm:mb-6 px-4 sm:px-6 py-2 sm:py-3 bg-white/20 backdrop-blur-sm rounded-full">
            <p className="text-white text-xs sm:text-sm font-medium whitespace-nowrap">Free Shipping on Orders $25+</p>
          </div>
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-8xl font-bold text-white mb-4 sm:mb-6 tracking-wider leading-tight">
            PURE NECTAR FOR YOUR<br />FEATHERED FRIENDS
          </h1>
          <p className="text-base sm:text-xl md:text-2xl text-white font-light mb-8 sm:mb-12 max-w-2xl mx-auto">
            Premium hummingbird nectar<br />Made with natural ingredients
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
            <Link
              to="/product/15oz"
              className="w-full sm:w-auto px-8 py-4 bg-black text-white rounded-full font-semibold hover:bg-black/80 transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <span className="w-8 h-8 bg-[#FF6B6B] rounded-full flex items-center justify-center">
                <i className="ri-sparkling-2-fill text-white text-sm"></i>
              </span>
              SHOP NOW
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/migration"
              className="w-full sm:w-auto px-8 py-4 bg-transparent border-2 border-white text-white rounded-full font-semibold hover:bg-white hover:text-black transition-all whitespace-nowrap cursor-pointer text-center"
            >
              LEARN ABOUT MIGRATION
            </Link>
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="bg-[#2C3E50] py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-4">
            <h2 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white leading-tight">
              CHOOSE YOUR <span className="text-[#FF6B6B]">PERFECT SIZE</span>
            </h2>
          </div>
          <p className="text-white/60 text-base sm:text-lg mb-10 sm:mb-16">
            Dry Nectar Mix — Ships Fast &amp; Light. Just add water &amp; stir!
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-10" data-product-shop>
            {products.map((product) => (
              <Link key={product.id} to={`/product/${product.id}`} className="group cursor-pointer">
                <div className="bg-[#F8F5F0] rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2">
                  <div className="p-6 sm:p-8 bg-white relative">
                    {product.badge && (
                      <span className="absolute top-4 right-4 px-3 py-1 bg-[#FF6B6B] text-white text-xs font-bold rounded-full whitespace-nowrap">
                        {product.badge}
                      </span>
                    )}
                    <img src={product.image} alt={product.name} className="w-full h-48 sm:h-64 object-contain" />
                  </div>
                  <div className="bg-[#2C3E50] p-6 sm:p-8">
                    <p className="text-[#FF6B6B] text-xs font-bold tracking-widest uppercase mb-1">Dry Nectar Mix</p>
                    <p className="text-white text-lg font-bold mb-1">{product.size}</p>
                    <p className="text-white/60 text-sm mb-1"><i className="ri-drop-line mr-1"></i>{product.makes}</p>
                    <p className="text-[#4ECDC4] text-sm mb-4"><i className="ri-truck-line mr-1"></i>{product.shipping}</p>
                    <div className="flex items-baseline gap-3 mb-4">
                      <p className="text-[#FF6B6B] text-3xl font-bold">${product.price}</p>
                      <p className="text-gray-400 text-sm line-through">${product.originalPrice}</p>
                    </div>
                    <button className="w-full px-6 py-3 bg-[#FF6B6B] text-white rounded-full font-semibold hover:bg-[#ff5252] transition-colors whitespace-nowrap">
                      Add to Cart
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-white py-10 sm:py-12 px-4 sm:px-6 border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-10 md:gap-16">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center bg-[#FFF0F0] rounded-full flex-shrink-0">
                <i className="ri-shield-check-fill text-2xl text-[#FF6B6B]"></i>
              </div>
              <div>
                <p className="font-bold text-[#2C3E50] text-sm sm:text-base">30-Day Money-Back Guarantee</p>
                <p className="text-gray-500 text-xs sm:text-sm">Not happy? We'll refund you, no questions asked.</p>
              </div>
            </div>
            <div className="hidden sm:block w-px h-12 bg-gray-200"></div>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center bg-[#F0FFF8] rounded-full flex-shrink-0">
                <i className="ri-lock-2-fill text-2xl text-[#4ECDC4]"></i>
              </div>
              <div>
                <p className="font-bold text-[#2C3E50] text-sm sm:text-base">Secure Checkout</p>
                <p className="text-gray-500 text-xs sm:text-sm">Your payment info is always protected.</p>
              </div>
            </div>
            <div className="hidden sm:block w-px h-12 bg-gray-200"></div>
            <div className="flex flex-col items-center gap-3">
              <p className="text-xs font-semibold text-gray-400 tracking-widest uppercase">We Accept</p>
              <div className="flex items-center gap-3">
                <div className="h-9 px-3 bg-[#1A1F71] rounded-md flex items-center justify-center">
                  <span className="text-white font-extrabold text-sm tracking-wider italic">VISA</span>
                </div>
                <div className="h-9 px-2 bg-white border border-gray-200 rounded-md flex items-center justify-center gap-1">
                  <div className="w-5 h-5 rounded-full bg-[#EB001B]"></div>
                  <div className="w-5 h-5 rounded-full bg-[#F79E1B] -ml-2.5 opacity-90"></div>
                </div>
                <div className="h-9 px-3 bg-[#003087] rounded-md flex items-center justify-center">
                  <span className="text-white font-extrabold text-sm">Pay<span className="text-[#009CDE]">Pal</span></span>
                </div>
                <div className="h-9 px-3 bg-black rounded-md flex items-center justify-center">
                  <span className="text-white font-semibold text-sm"> Pay</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-[#F8F9FA] py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-bold text-center mb-10 sm:mb-16">
            WHY HUMMINGBIRDS <span className="text-[#4ECDC4]">LOVE US</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative rounded-3xl overflow-hidden h-64 sm:h-96 group cursor-pointer">
              <img
                src="https://readdy.ai/api/search-image?query=close%20up%20hummingbird%20drinking%20nectar%20from%20vibrant%20red%20flower%20natural%20habitat%20macro%20photography%20beautiful%20colors%20soft%20focus%20professional%20wildlife%20shot%20stunning%20detail&width=800&height=600&seq=feature1&orientation=landscape"
                alt="Natural Ingredients"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-6 left-6 px-5 py-3 bg-black/70 backdrop-blur-sm rounded-2xl">
                <p className="text-white font-semibold text-base sm:text-lg">100% Natural Ingredients</p>
              </div>
            </div>
            <div className="flex flex-col gap-6">
              <div className="bg-[#C8D5B9] rounded-3xl p-8 sm:p-10 flex items-center gap-6 group hover:shadow-lg transition-all cursor-pointer">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-drop-line text-2xl text-[#4ECDC4]"></i>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#2C3E50] mb-1">Easy Mix Formula</h3>
                  <p className="text-[#2C3E50]/70 text-sm sm:text-base">Dissolves instantly in water</p>
                </div>
              </div>
              <div className="bg-[#FFD4D4] rounded-3xl p-8 sm:p-10 flex items-center gap-6 group hover:shadow-lg transition-all cursor-pointer">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-check-line text-2xl text-[#FF6B6B]"></i>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#2C3E50] mb-1">Long Shelf Life</h3>
                  <p className="text-[#2C3E50]/70 text-sm sm:text-base">Stays fresh for months</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="bg-white py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-serif text-center mb-10 sm:mb-16 text-[#2C3E50]">(What Bird Lovers Say)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {reviews.map((review) => (
              <div key={review.id} className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2C3E50] rounded-full mb-5">
                  <i className="ri-star-fill text-[#FF6B6B]"></i>
                  <span className="text-white font-bold">5.0</span>
                </div>
                <p className="text-[#2C3E50] text-base sm:text-lg mb-6 font-serif leading-relaxed">"{review.text}"</p>
                <div className="flex items-center gap-4">
                  <img src={review.avatar} alt={review.name} className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover flex-shrink-0" />
                  <div>
                    <p className="font-bold text-[#2C3E50]">{review.name}</p>
                    <p className="text-gray-500 text-sm">{review.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-[#F8F9FA] py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <span className="inline-block px-5 py-2 bg-[#FFD4D4] text-[#FF6B6B] text-xs font-bold tracking-widest uppercase rounded-full mb-4">
              Got Questions?
            </span>
            <h2 className="text-3xl sm:text-5xl font-bold text-[#2C3E50] leading-tight">
              FREQUENTLY ASKED <span className="text-[#FF6B6B]">QUESTIONS</span>
            </h2>
            <p className="text-gray-500 mt-4 text-base sm:text-lg">Everything you need to know about Front Porch Nectar.</p>
          </div>
          <div className="flex flex-col gap-4">
            {faqs.map((faq) => (
              <div key={faq.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden transition-all">
                <button
                  onClick={() => toggleFaq(faq.id)}
                  className="w-full flex items-center justify-between px-5 sm:px-8 py-5 sm:py-6 text-left cursor-pointer group"
                >
                  <span className="text-[#2C3E50] font-semibold text-base sm:text-lg pr-4">{faq.question}</span>
                  <div className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${openFaq === faq.id ? 'bg-[#FF6B6B] text-white' : 'bg-[#F8F9FA] text-[#2C3E50] group-hover:bg-[#FFD4D4]'}`}>
                    <i className={`ri-${openFaq === faq.id ? 'subtract' : 'add'}-line text-lg`}></i>
                  </div>
                </button>
                {openFaq === faq.id && (
                  <div className="px-5 sm:px-8 pb-6">
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-10 sm:mt-12 text-center">
            <p className="text-gray-500 mb-4">Still have questions?</p>
            <a
              href="mailto:support@frontporchnectar.com"
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#2C3E50] text-white rounded-full font-semibold hover:bg-[#2C3E50]/80 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line"></i>
              Email Us
            </a>
          </div>
        </div>
      </section>

      {/* Migration CTA */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53]"></div>
        <div className="relative z-10 max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-10">
          <div className="flex-1 text-center lg:text-left">
            <h2 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
              TRACK HUMMINGBIRD<br />MIGRATION
            </h2>
            <p className="text-white text-base sm:text-xl mb-6 sm:mb-8 max-w-xl mx-auto lg:mx-0">
              Find out when hummingbirds arrive in your state and prepare your feeders for their arrival
            </p>
            <Link
              to="/migration"
              className="inline-flex items-center gap-3 px-8 sm:px-10 py-4 bg-white text-[#2C3E50] rounded-full font-bold hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-map-pin-line text-xl"></i>
              VIEW MIGRATION MAP
            </Link>
          </div>
          <div className="hidden lg:block flex-1">
            <img
              src="https://readdy.ai/api/search-image?query=illustrated%20migration%20map%20of%20north%20america%20with%20flight%20path%20arrows%20showing%20hummingbird%20migration%20routes%20colorful%20artistic%20style%20clean%20design%20educational%20infographic&width=600&height=400&seq=migrationmap&orientation=landscape"
              alt="Migration Map"
              className="w-full h-auto rounded-2xl"
            />
          </div>
        </div>
      </section>

      {/* Email Newsletter Section */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden bg-[#2C3E50]">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=seamless%20pattern%20of%20hummingbirds%20and%20tropical%20flowers%20watercolor%20style%20soft%20pastel%20colors%20elegant%20botanical%20illustration%20repeating%20motif&width=1200&height=600&seq=newsletter-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <div className="w-14 h-14 flex items-center justify-center bg-[#FF6B6B]/20 rounded-full mx-auto mb-5">
            <i className="ri-mail-send-line text-2xl text-[#FF6B6B]"></i>
          </div>
          <span className="inline-block px-5 py-2 bg-[#FF6B6B]/20 text-[#FF6B6B] text-xs font-bold tracking-widest uppercase rounded-full mb-4">
            Join the Flock
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Get Nectar Tips &amp;<br />Migration Dates
          </h2>
          <p className="text-white/60 text-base sm:text-lg mb-8 sm:mb-10 leading-relaxed">
            We'll send you seasonal feeding guides, hummingbird migration alerts for your region, and exclusive subscriber-only discounts. No spam — ever.
          </p>
          {!footerSubmitted ? (
            <form
              data-readdy-form
              onSubmit={handleFooterSubmit}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                type="email"
                name="email"
                required
                value={footerEmail}
                onChange={e => setFooterEmail(e.target.value)}
                placeholder="Your email address"
                className="flex-1 px-5 sm:px-6 py-4 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:border-[#FF6B6B] transition-colors"
              />
              <button
                type="submit"
                disabled={footerSubmitting}
                className="px-6 sm:px-8 py-4 bg-[#FF6B6B] text-white rounded-full font-semibold text-sm hover:bg-[#ff5252] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {footerSubmitting ? 'Subscribing...' : 'Subscribe Free'}
              </button>
            </form>
          ) : (
            <div className="flex items-center justify-center gap-3 py-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#FF6B6B]/20 rounded-full">
                <i className="ri-check-line text-xl text-[#FF6B6B]"></i>
              </div>
              <p className="text-white font-semibold text-lg">You're subscribed! Watch your inbox 🐦</p>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 mt-8 sm:mt-10">
            <div className="flex items-center gap-2 text-white/50 text-sm">
              <i className="ri-user-heart-line text-[#FF6B6B]"></i>
              <span>12,000+ subscribers</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-white/20"></div>
            <div className="flex items-center gap-2 text-white/50 text-sm">
              <i className="ri-spam-2-line text-[#FF6B6B]"></i>
              <span>Zero spam, ever</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-white/20"></div>
            <div className="flex items-center gap-2 text-white/50 text-sm">
              <i className="ri-close-circle-line text-[#FF6B6B]"></i>
              <span>Unsubscribe anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#FF6B6B] text-white py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 sm:gap-12 mb-10 sm:mb-12">
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/b04a437c-0005-402a-b32c-9c2676d2dc8b.png"
                alt="Front Porch Nectar"
                className="h-14 sm:h-16 w-auto max-w-[180px] object-contain mb-5"
              />
              <p className="text-white/90 leading-relaxed mb-5 text-sm sm:text-base">
                Premium hummingbird nectar made with 100% natural ingredients. Trusted by bird lovers across North America for over a decade.
              </p>
              <div className="flex gap-3">
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#FF6B6B] transition-all cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#FF6B6B] transition-all cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#FF6B6B] transition-all cursor-pointer">
                  <i className="ri-twitter-x-line"></i>
                </a>
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#FF6B6B] transition-all cursor-pointer">
                  <i className="ri-youtube-fill"></i>
                </a>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold mb-4 tracking-wider">CONTACT</h3>
              <p className="mb-6 text-sm sm:text-base break-all">support@frontporchnectar.com</p>
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <img
                src="https://public.readdy.ai/ai/img_res/edited_01644ccd692d553779e0b0eb9245e76b_93373ede.jpg"
                alt="Hummingbird at feeder"
                className="w-full h-56 sm:h-80 object-cover rounded-2xl"
              />
            </div>
          </div>
          <div className="border-t border-white/20 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-white/80 text-sm text-center sm:text-left">© 2025 Front Porch Nectar. All rights reserved</p>
            <div className="flex gap-4 sm:gap-6 text-sm flex-wrap justify-center">
              <a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer">Privacy Policy</a>
              <span className="text-white/40">/</span>
              <a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer">Terms</a>
              <span className="text-white/40">/</span>
              <a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer">Shipping</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
